import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { Upload, Trash2, Download, AlertCircle, FileText, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';


const DOCUMENT_TYPES = [
  'DNI',
  'TIP Seguridad Privada',
  'Seguridad Social',
  'Certificado Cuenta Bancaria',
  'Otro'
];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Por favor, inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const getDocTypeBadgeColor = (type) => {
  switch (type) {
    case 'DNI': return 'bg-blue-100 text-blue-800';
    case 'TIP Seguridad Privada': return 'bg-amber-100 text-amber-800';
    case 'Seguridad Social': return 'bg-emerald-100 text-emerald-800';
    case 'Certificado Cuenta Bancaria': return 'bg-purple-100 text-purple-800';
    default: return 'bg-orange-100 text-orange-800';
  }
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'approved': return { label: 'Aprobado', className: 'bg-green-100 text-green-700' };
    case 'reviewed': return { label: 'Revisado', className: 'bg-blue-100 text-blue-700' };
    case 'rejected': return { label: 'Rechazado', className: 'bg-red-100 text-red-700' };
    default: return { label: 'Pendiente', className: 'bg-orange-100 text-orange-700' };
  }
};

export default function WorkerDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    document_type: '',
    file: null
  });

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await API.get('/document-categories');
      setCategories(Array.isArray(res.data) ? res.data : DOCUMENT_TYPES);
    } catch {
      setCategories(DOCUMENT_TYPES);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await API.get(`/documents`);
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.document_type || !formData.file) {
      setError('Selecciona un tipo de documento y un archivo');
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', formData.file);
      
      await API.post(
        `/documents?document_type=${encodeURIComponent(formData.document_type)}`,
        data,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setDialogOpen(false);
      setFormData({ document_type: '', file: null });
      fetchDocuments();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!deleteId) return;
    try {
      await API.delete(`/documents/${deleteId}`);
      setDeleteId(null);
      fetchDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await API.get(`/documents/download/${doc.id}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  // Group documents by type
  const allTypes = [...new Set([...categories, ...documents.map(d => d.document_type)])];
  const documentsByType = allTypes.reduce((acc, type) => {
    acc[type] = documents.filter(d => d.document_type === type);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-100 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="worker-documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900" >
            Mis Documentos
          </h1>
          <p className="text-gray-500 mt-1">Sube y gestiona tu documentación</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-md shadow-orange-200" data-testid="upload-document-btn">
              <Upload className="w-4 h-4 mr-2" />
              Subir Documento
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle >Subir Documento</DialogTitle>
              <DialogDescription>Selecciona el tipo de documento y el archivo a subir.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUploadDocument} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Tipo de documento</Label>
                <Select
                  value={formData.document_type}
                  onValueChange={(value) => setFormData({ ...formData, document_type: value })}
                >
                  <SelectTrigger data-testid="document-type-select">
                    <SelectValue placeholder="Selecciona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Archivo (PDF o imagen)</Label>
                <Input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                  className="cursor-pointer"
                  data-testid="document-file-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-md shadow-orange-200" 
                  disabled={uploading}
                  data-testid="submit-document-btn"
                >
                  {uploading ? 'Subiendo...' : 'Subir'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Document Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {allTypes.map((type) => {
          const docs = documentsByType[type];
          const hasDoc = docs.length > 0;
          return (
            <div 
              key={type}
              className={`bg-white border rounded-xl p-6 ${hasDoc ? 'border-emerald-200' : 'border-orange-100 border-dashed'}`}
            >
              <div className="flex items-center justify-between mb-4">
                <Badge className={getDocTypeBadgeColor(type)}>{type}</Badge>
                {hasDoc && (
                  <Badge className={getStatusBadge(docs[0]?.validation_status).className}>
                    {getStatusBadge(docs[0]?.validation_status).label}
                  </Badge>
                )}
              </div>
              {hasDoc ? (
                <div className="space-y-2">
                  {docs.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-2 bg-[#FFF8F4] rounded">
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-700 truncate block">{doc.original_filename}</span>
                        {doc.expiry_date && (
                          <span className={`text-xs ${new Date(doc.expiry_date) <= new Date(Date.now() + 30*86400000) ? 'text-amber-600' : 'text-gray-400'}`}>
                            {new Date(doc.expiry_date) <= new Date(Date.now() + 30*86400000) && <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
                            Vence: {new Date(doc.expiry_date).toLocaleDateString('es-ES')}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} data-testid={`download-${doc.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleteId(doc.id)} data-testid={`delete-${doc.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No has subido este documento</p>
              )}
            </div>
          );
        })}
      </div>

      {/* All Documents Table */}
      <div className="bg-white border border-orange-100 rounded-xl">
        <div className="p-6 border-b border-orange-100">
          <h2 className="text-lg font-bold text-gray-900" >
            Todos mis documentos
          </h2>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-orange-100 bg-[#FFF8F4]">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Tipo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Archivo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Estado</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Fecha</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No tienes documentos subidos
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow key={doc.id} className="hover:bg-orange-50/50" data-testid={`document-row-${doc.id}`}>
                    <TableCell>
                      <Badge className={getDocTypeBadgeColor(doc.document_type)}>
                        {doc.document_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium text-gray-900">{doc.original_filename}</TableCell>
                    <TableCell>
                      <Badge className={getStatusBadge(doc.validation_status).className}>
                        {getStatusBadge(doc.validation_status).label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-600 text-sm">
                      {new Date(doc.created_at).toLocaleDateString('es-ES')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-600 hover:text-gray-900 hover:bg-orange-50"
                          onClick={() => handleDownload(doc)}
                          data-testid={`download-document-${doc.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteId(doc.id)}
                          data-testid={`delete-document-${doc.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará el documento de forma permanente. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteDocument}
              data-testid="confirm-delete-document"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
